import { IncidentGracePeriod } from "@rightship-tech/common-ui/models/incidents/incident.models";
import { InspectionStateEnum } from "@rightship-tech/common-ui/models/rightship-inspections/rightship-inspections.models";
import { BaseValidationResponse } from "@rightship-tech/common-ui/models/validations.models";
import { VesselDetailModel } from "./vessel.models";
import { RiskAnalysisRuleModel } from "./rule.models";
import { computedFrom } from "aurelia-binding";

export class DueDiligenceClientModel {
    viewModel?: string;
    fullVetRequestedDate?: Date;
    portCountryOfLoadUnknown: any;
    portCountryOfDischargeUnknown: any;
    vetId: number;
    requestedByEmail?: string;
    aiSummaryFile?: string;
    customerId?: number;
    isPassedToRSVettingQueue?: boolean;
    businessUnitId?: number;
    docCompanyId: number;
    docCompanyName?: string;
    otherComments: string;
    vesselId: number;
    statusId: number;
    vettingTypeId?: number;
    status: string;
    statusHistoryId: number;
    yearOfConstruction?: number;
    dateRequested: Date;
    dateOfDeparture?: Date;
    dateOfArrival?: Date;
    isTest?: boolean;
    built?: Date;
    deadWeightTonnage: number;
    requestedBy: string;
    phoneNumbers: string[];
    cargo: string;
    portOfDischarge: string;
    portOfLoad: string;
    internationalMaritimeOrganizationNumber: string;
    cargoTypeCategoryId: number;
    documentOfComplianceEmail: string;
    vetRiskScores?: VetRiskScoreModel[];
    vehicleIdentificationNumber: string;
    europeanNumberOfIdentification: string;
    vesselName: string;
    vesselImo: string;
    vesselType: string;
    incoTerms: string;
    vetComments: string;
    berth: string;
    tasks: VetTaskClientModel[];
    statusHistory: VetStatusHistoryClientModel[];
    portOfLoadId?: number;
    portOfDischargeId?: number;
    cargoId?: number;
    incoTermsId?: number;
    requestedByBusinessUnitId?: number;
    requestedByBusinessUnit?: string;
    portCountryOfLoad?: string;
    portCountryOfDischarge?: string;
    requestedByUserId?: number;
    vetForSTS?: boolean;
    charterTypeId?: boolean;
    starRating: number;
    rating: string;
    documentsCount?: number;
    correspondenceCount?: number;
    privateNotesCount?: number;
    transactionNumber?: string;
    ageForInspection?: number;
    dwtForInspection?: number;
    failedRightShipCriteria?: boolean;
    failedCustomerCriteria?: boolean;
    ruleCollectionName?: string;
    daysRemainingAtVetOutcome?: number;
    daysToAgeRequirementAtVetOutcome?: number;
    rightShipInspectionExpiryDateAtVetOutcome?: Date;
    inspectionStateAtVetOutcome?: number;
    crewWelfareValidUntilAtVetOutcome?: Date;
    crewWelfareStatusAtVetOutcome?: string;
    starRatingAtVetOutcome?: number;
    safetyRatingTypeAtVetOutcome?: number;
    ratingAtVetOutcome?: string;
    ratingPlusAtVetOutcome?: boolean;
    isRatingVerifiedAtVetOutcome?: boolean;
    docSubscoreAtVetOutcome?: number;
    requestedOnBehalfOfCompanyName: string;
    requestedOnBehalfOfName: string;
    requestedOnBehalfOfEmail: string;
    incidentGracePeriodsAtVetOutcome?: IncidentGracePeriod[];
    vesselDateOfBuild: Date;
    departureDate?: Date;
    arrivalDate?: Date;
    outcomeRequiredBy?: Date;
    createdDate: Date;
    cargoTypeId: number;

    set vetStatus(value: string)
    {
        this.status = value;
    }

    @computedFrom("status")
    get vetStatus(): string
    {
        return this.status;
    }

    set vetStatusId(value: number)
    {
        this.statusId = value;
    }

    @computedFrom("statusId")
    get vetStatusId(): number
    {
        return this.statusId;
    }

    set vetStatusHistory(value: VetStatusHistoryClientModel[])
    {
        this.statusHistory = value;
    }

    @computedFrom("statusHistory")
    get vetStatusHistory(): VetStatusHistoryClientModel[]
    {
        return this.statusHistory;
    }

    set vetStatusHistoryId(value: number)
    {
        this.statusHistoryId = value;
    }

    @computedFrom("statusHistoryId")
    get vetStatusHistoryId(): number
    {
        return this.statusHistoryId;
    }

    set vetTasks(value: VetTaskClientModel[])
    {
        this.tasks = value;
    }

    @computedFrom("tasks")
    get vetTasks(): VetTaskClientModel[]
    {
        return this.tasks;
    }
}

export interface GetLastDueDiligenceResponse {
    hasDueDiligence: boolean;
    lastDueDiligence: DueDiligenceClientModel;
}

export interface VetStatusHistoryClientModel {
    createdDate: Date;
    createdBy: string;
    status: string;
    previousStatusId?: number;
    statusId: number;
    user: string;
    reasonForCancellation: string;
    comment: string;
    fullComment: string;
    subtaskTypes: number[];
    isAutoCancelled?: boolean;
    failedRightShipCriteria?: boolean;
    failedCustomerCriteria?: boolean;
    statusCreatedById?: number;
}

export interface VetRiskScoreModel {
    riskScore: { nominator: number; denominator: number; result: number; };
    title: string;
    name: string;
    riskLevel: string;
    riskLevelId: number;
    sequence: number;
    riskCategoryId: number;

    countLowFail: number;
    countLowPending: number;
    countLowPass: number;
    countMediumFail: number;
    countMediumPending: number;
    countMediumPass: number;
    countHighFail: number;
    countHighPending: number;
    countHighPass: number;
    countOraFail: number;
    countOraPending: number;
    countOraPass: number;
}

export interface VetTaskClientModel {
    isOverriddenAtCustomer: boolean;
    element?: HTMLElement;
    prefetchRuleActionId?: number;
    hasPendingPrefetchData?: boolean;
    vettingCommentDateModified?: Date;
    createdDate?: Date;
    thirdPartyPayload?: string;
    thirdPartyPayloadDate?: Date;
    passedVettingComment: string;
    failedVettingComment: string;
    naVettingComment: string;
    infoVettingComment: string;
    ruleMeta: string;
    forceIncludeComment: boolean;
    hasThirdParty?: boolean;
    isReviewed: boolean;
    displaySections: any[];
    vettingCommentAction?: number;
    prefetchTimeToWaitMinutes?: number;
    hasPscInspections: boolean;
    hasCap: boolean;
    hasGhgVerifications: boolean;
    hasInspections: boolean;
    hasCfrs: boolean;
    hasIncidents: boolean;
    hasForms: boolean;
    hasTq?: boolean;
    hasListItems: boolean;
    hasRsListItems: boolean;
    vetSuperVetTaskOutcomeId: VetRuleOutcomeStepEnum;
    lastModifiedDate?: Date;
    vetSuperLastModifiedDate?: Date;
    ruleLastModifiedDate?: Date;
    vetSuperLastModifiedName?: string;
    rightshipPayload?: string;
    vetTaskId: number;
    ruleConfigId?: number;
    newUpdateDate?: Date;
    description: string;
    title: string;
    category: string;
    categoryIsCustomer?: boolean;
    actionToTake: string;
    reason: string;
    vetTaskOutcomeId: number;
    canViewDisplayResultsInOverview: boolean;
    canViewDisplayResultsInCriteria: boolean;
    isCritical?: boolean;
    categoryId: number;
    isManual: boolean;
    computedOutcome: boolean;
    ruleActionId: number;
    workflowStateId: number;
    systemError: string;
    ruleSubtaskTypes: number[];
    pluginEntityTypeId: number;
    sequenceNumber?: number;
    ruleCode?: string;
    vesselApplicationFormId?: number;
    isVisibleToRsVetSupersOnly: boolean;
    isInfoOnlyRule: boolean;
    prefetchDataPersistedOn: Date;
    metaData: VetTaskMetaData;
    screeningOverviewDisplayVisibilityId: number;
    screeningCriteriaDisplayVisibilityId: number;
    vettingOverviewDisplayVisibilityId: number;
    vettingCriteriaDisplayVisibilityId: number;
}

export interface VetTaskMetaData {
    category: string;
    name: string;
    expiryDate?: Date;
}

export interface GetCollaboratorsResponse {
    collaborators: CollaboratorEmail[];
    businessUnitName: string;
    customerName: string;
}

export interface CollaboratorEmail {
    email: string;
}

export interface VetRecentActivityClientModel {
    entityTypeId: number;
    isRfi: boolean;
    isForm: boolean;
    isAwaitingResponse: boolean;
    isPendingReview: boolean;
    isOpen: boolean;
    entityId: number;
    statusId: number;
    title: string;
    status: string;
    statusSnippet: string;
    statusSnippet2: string;
}

export interface VetRuleRunClientModel {
    isSelected?: boolean;
    vettingRuleRunId: number;
    date: Date;
    action: string;
    vetStatus: string;
    businessUnitId: number;
    items: VetRuleRunItemClientModel[];
    risks: VetRuleRunRiskClientModel[];
}

export interface VetRuleRunRiskClientModel {
    riskCategory: string;
    riskLevel: string;
    riskLevelId: number;
    riskScore: { nominator: number, denominator: number, result: number };

    countLowFail: number;
    countLowPending: number;
    countLowPass: number;
    countMediumFail: number;
    countMediumPending: number;
    countMediumPass: number;
    countHighFail: number;
    countHighPending: number;
    countHighPass: number;
    countOraFail: number;
    countOraPending: number;
    countOraPass: number;
    countChangedFail: number;
    countChangedPending: number;
    countChangedPass: number;
}

export interface VetRuleRunItemClientModel {
    vetId: number;
    vetTaskId: number;
    wasActive: boolean;
    isActive: boolean;
    previousVetTaskOutcome: number;
    vetTaskOutcome: number;
    sequenceNumber: number;
    vettingRuleRunId: number;
    prefetchRuleActionId: number;
    vettingRuleRunItemId: number;
    date: Date;
    ruleCode: string;
    title: string;
    description: string;
    scores: VetRuleRunItemScoreClientModel[];
    previousScores: VetRuleRunItemScoreClientModel[];
    hasSystemError: boolean;
    hasPreviousSystemError: boolean;
    hasPendingPrefetchData?: boolean;
    hasListItems?: boolean;
    hasRsListItems?: boolean;
    hasCap?: boolean;
    canViewDisplayResultsInOverview?: boolean;
}

export interface VetRuleRunItemScoreClientModel {
    vettingRuleRunItemId: number;
    riskCategory: string;
    riskLevel: string;
    riskLevelId: number;
}

export interface RatingsSummaryModel  {
    inspectionState?: InspectionStateEnum;
    vesselEnergySavingEquipments: VesselEnergySavingEquipmentClientModel[];
    safetyRatingType: number;
    systemRuleResult: SystemRuleResultSummaryModel[];
    starRating: number;
    safetyRatingReviewId?: number;
    isVerified: boolean;
    rating: string;
    vesselId: number;
    score: number;
    docName: string;
    flagId: number;
    flagName: string;
    classId: number;
    className: string;
    sizeScore?: number;
    ratingPlus?: boolean;
    hasRating?: boolean;
    systemRuleLastExecutedDate?: Date;
    systemRuleLastExecutedStatus?: string;
    systemRuleCanRequestReview?: boolean;
    isSanctioned: boolean;
    isFlagFake: boolean;
    ghgAdditionalMetadataJSON: string;
    latestShipIndex: string;
    incidentGracePeriods: IncidentGracePeriod[];
    isInGeoPoliticalZone: boolean;
    hasGeopoliticalConflict: boolean;
}

export interface VesselEnergySavingEquipmentClientModel {
    energySavingEquipmentCategoryId: number;
    energySavingEquipmentId: number;
    implementedDate: string;
}

export interface SystemRuleResultSummaryModel {
    canRequestReview?: boolean;
    code?: string;
    banner?: string;
    display?: string;
    group?: string;
    description?: string;
    actionTypeId?: number;
    resolveRuleDescription?: string;
}

export enum VetRuleOutcomeStepEnum {
    Pass = 1,
    Fail = 2,
    NA = 3
}

export enum VettingCommentActionEnum {
    AlwaysIncludeComment = 1,
    NeverIncludeComment = 2,
    OnlyShowIfFailed = 3
}

export enum VetStatusEnum {
    Processing = 1,
    DD_AwaitingResponse = 301,
    DD_Acceptable = 302,
    DD_Unacceptable = 303,
    DD_DueDiligenceReassignment = 304,
    DD_RequireIntervention = 305,
    DD_RequireRightShipIntervention = 306,
    DD_PendingSeniorApproval = 307,
    DD_UnderReview = 308
}

export enum VettingStepEnum {
    VetOverview = 1,
    VetMessageCenter = 2,
    VetDocuments = 3,
    VetComments = 4,
    VetVesselCorrespondence = 5,
    HistoryTrail = 7,
    VetRulesRun = 8,
    EmailHistory = 9,
    Notes = 10
}

export enum VetRunRiskLevelsEnum {
    Low = 1,
    Medium = 2,
    High = 3,
    Ora = 4
}

export enum VetTaskOutcomeEnum {
    Pass = 1,
    Fail = 2,
    NA = 3
}

export enum VettingActionStepEnum {
    ManualAction = 1,
    RequireTQForm = 2,
    RequireInspection = 3,
    RequirePSCReview = 4,
    RequireIncidentReview = 5,

    VesselHealthCheckCritical = 200
}

export interface ITemplateMetaPlugin {
    viewTitle: string;
    isValid: () => Promise<boolean>;
    getMetaModel: () => any;
    readonly disableSubmit: boolean;
    readonly canSubmit: boolean;
}

export interface DueDiligenceRequestModel {
    vesselId: number;
    portFromId: number;
    portToId: number;
    departureDate?: Date;
    arrivalDate?: Date;
    cargoTypeId?: number;
    incoTermId?: number;
    comments?: string;
    berth?: string;
    dischargeBerth?: string;
    vetId?: number;
    notifyComplianceTeam?: boolean;
    vetForSTS?: boolean;
    isTest?: boolean;
    isPassedToRSVettingQueue?: boolean;
    charterTypeId?: number;
    vetForBST?: boolean;
    businessUnitId?: number;
    transactionNumber?: string;
    parentVetId?: number;
    ruleCollectionId: number;
    timeCharterStartDate: Date;
    timeCharterEndDate: Date;
    outcomeRequiredBy: Date;
    identifier: string;
}

export interface SaveDueDiligenceResponse extends BaseValidationResponse {
    generatedId?: number;
}

export type DueDiligenceDataModel = {
    vesselId: number;
    vessel: VesselDetailModel;
    parentVetId?: number;
    ruleCollectionId: number;
    ruleCollectionInputVM: string;
    customerId: number;
    businessUnitId: number;
    isTest: boolean;
}

export interface CustomerDueDiligenceEmailModel {
    Id: number;
    email: string;
}
export interface CustomerDueDiligenceApprovalEmailModel extends CustomerDueDiligenceEmailModel { }

export interface CustomerComplianceCheckEmailModel extends CustomerDueDiligenceEmailModel { }

export interface GetSeniorApproverResponse {
    seniorApprovers: CustomerDueDiligenceApprovalEmailModel[];
}

export interface GetComplianceCheckResponse {
    complianceChecks: CustomerComplianceCheckEmailModel[];
}

export class GetDueDiligenceRiskScores {
    public isSuccess: boolean;
    public errorMessage: string;
    public riskScores: DueDiligenceRiskScoreClientModel[];
}

export class DueDiligenceRiskScoreClientModel {
    public name: string;
    public info: string;
    public riskLevelId: number;
    public riskCategoryId: number;
    public countLowPass: number;
    public countLowFail: number;
    public countLowPending: number;
    public countMediumPass: number;
    public countMediumFail: number;
    public countMediumPending: number;
    public countHighPass: number;
    public countHighFail: number;
    public countHighPending: number;
    public countOraPass: number;
    public countOraFail: number;
    public countOraPending: number;
    public riskLevel: string;

    public get title(): string {
        switch (this.name) {
            case "All": return "Overall Risk Score";
            case "Critical": return "Critical Score";
            case "Soft": return "Soft Score";
            case "Environmental": return "Environmental Risk";
            case "Social": return "Social Risk";
            case "Safety": return "Safety Risk";
            default: return this.name;
        }
    }

    public get sequence(): number {
        switch (this.name) {
            case "All": return 1;
            case "Critical": return 2;
            case "Soft": return 3;
            case "Environmental": return 4;
            case "Social": return 5;
            case "Safety": return 6;
            default: return 7;
        }
    }
}

export interface GetRiskAnalysisRulesRequest {
    vetId: number;
    riskCategoryId: number;
}

export interface GetRiskAnalysisRulesResponse {
    rules: RiskAnalysisRuleModel[];
}